import { extractInstagramUrlShortcode } from './instagram-url.util';

/**
 * URL Extractor Utility (Frontend)
 *
 * Extracts platform and post ID/code from social media URLs.
 * Supports Instagram, TikTok, YouTube, and Facebook URLs.
 */

export type Platform = 'instagram' | 'tiktok' | 'youtube' | 'facebook';

/**
 * TikTok share-sheet short links: vm.tiktok.com/CODE, vt.tiktok.com/CODE, and
 * tiktok.com/t/CODE. These carry no video ID in the URL — they HTTP-redirect to
 * the canonical `tiktok.com/@user/video/{id}` form — so an id can't be extracted
 * client-side. The backend (sms/utils/url-resolver.util.ts) follows the redirect,
 * so we treat these as valid post URLs even though extractPostIdFromUrl returns
 * null for them. Mirrors the backend TIKTOK_SHORT_LINK pattern.
 */
export function isTikTokShortLink(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }
  return /^https?:\/\/(?:(?:www\.)?tiktok\.com\/t\/|(?:www\.)?(?:vm|vt)\.tiktok\.com\/)[^/?#]+/i.test(
    url.trim()
  );
}

/**
 * Normalize URL by removing query parameters, fragments, and trimming whitespace
 */
export function normalizeUrl(url: string): string {
  if (!url || typeof url !== 'string') {
    return '';
  }

  // Trim whitespace
  let normalized = url.trim();

  // Remove fragments (#)
  const fragmentIndex = normalized.indexOf('#');
  if (fragmentIndex !== -1) {
    normalized = normalized.substring(0, fragmentIndex);
  }

  // Remove query parameters (?)
  const queryIndex = normalized.indexOf('?');
  if (queryIndex !== -1) {
    normalized = normalized.substring(0, queryIndex);
  }

  // Remove trailing slashes
  normalized = normalized.replace(/\/+$/, '');

  return normalized;
}

/**
 * Extract platform from URL
 */
export function extractPlatformFromUrl(url: string | null | undefined): Platform | null {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const lowerUrl = url.toLowerCase();

  // Check for Instagram
  if (lowerUrl.includes('instagram') || lowerUrl.includes('instagr.am')) {
    return 'instagram';
  }

  // Check for TikTok
  if (lowerUrl.includes('tiktok')) {
    return 'tiktok';
  }

  // Check for YouTube
  if (lowerUrl.includes('youtube') || lowerUrl.includes('youtu.be')) {
    return 'youtube';
  }

  // Check for Facebook
  if (
    lowerUrl.includes('facebook.com') ||
    lowerUrl.includes('fb.watch') ||
    lowerUrl.includes('fb.com')
  ) {
    return 'facebook';
  }

  return null;
}

/**
 * Extracts the post identifier (ID or code) from a social media URL for a given platform.
 *
 * The function expects a full post URL for the specified platform and returns the
 * platform-specific identifier used to reference that post. If the URL does not
 * match any of the supported patterns for the given platform, `null` is returned.
 *
 * Supported URL formats (query parameters and fragments may be present; they are
 * typically removed via {@link normalizeUrl} before calling this function):
 *
 * - Instagram:
 *   - `https://www.instagram.com/p/{CODE}/`
 *   - `https://instagram.com/p/{CODE}`
 *   - `https://www.instagram.com/reel/{CODE}/`
 *   - `https://instagram.com/reel/{CODE}`
 *   - `https://www.instagram.com/reels/{CODE}/` (plural share-sheet form)
 *   - `https://www.instagram.com/{username}/reel/{CODE}/` (attributed form)
 *   The `{CODE}` shortcode is returned regardless of the `/p/`, `/reel/`, or
 *   `/reels/` prefix or an optional leading `/{username}/` segment.
 *
 * - TikTok:
 *   - `https://www.tiktok.com/@{username}/video/{ID}`
 *   - `https://tiktok.com/@{username}/video/{ID}`
 *   The numeric `{ID}` segment after `/video/` is returned.
 *
 * - YouTube:
 *   - `https://www.youtube.com/watch?v={VIDEO_ID}`
 *   - `https://youtube.com/watch?v={VIDEO_ID}`
 *   - `https://youtu.be/{VIDEO_ID}`
 *   The `{VIDEO_ID}` value from the `v` query parameter or the path (for `youtu.be`)
 *   is returned.
 *
 * - Facebook:
 *   - `https://www.facebook.com/{page}/posts/{POST_ID}`
 *   - `https://www.facebook.com/watch?v={VIDEO_ID}`
 *   - `https://fb.watch/{CODE}/`
 *   - `https://fb.com/watch?v={VIDEO_ID}`
 *   Depending on the URL pattern, the `{POST_ID}`, `{VIDEO_ID}`, or `{CODE}` segment
 *   is returned.
 *
 * @param url - The raw or normalized URL string to extract the post identifier from.
 * @param platform - The detected platform corresponding to the URL.
 * @returns The extracted post identifier for the given platform, or `null` if the
 *          URL does not match any supported pattern for that platform.
 */
export function extractPostIdFromUrl(url: string, platform: Platform): string | null {
  if (!url || typeof url !== 'string') {
    return null;
  }

  switch (platform) {
    case 'instagram': {
      return extractInstagramUrlShortcode(url);
    }

    case 'tiktok': {
      // TikTok: https://www.tiktok.com/@username/video/ID
      const tiktokMatch = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/i);
      if (tiktokMatch && tiktokMatch[1]) {
        return tiktokMatch[1];
      }
      return null;
    }

    case 'youtube': {
      // YouTube: https://www.youtube.com/watch?v=VIDEO_ID or https://youtu.be/VIDEO_ID or https://www.youtube.com/shorts/VIDEO_ID
      const youtubeWatchMatch = url.match(/[?&]v=([^&]+)/i);
      if (youtubeWatchMatch && youtubeWatchMatch[1]) {
        return youtubeWatchMatch[1];
      }
      const youtubeShortMatch = url.match(/youtu\.be\/([^/?]+)/i);
      if (youtubeShortMatch && youtubeShortMatch[1]) {
        return youtubeShortMatch[1];
      }
      const youtubeShortsMatch = url.match(/youtube\.com\/shorts\/([^/?]+)/i);
      if (youtubeShortsMatch && youtubeShortsMatch[1]) {
        return youtubeShortsMatch[1];
      }
      return null;
    }

    case 'facebook': {
      // Facebook: https://www.facebook.com/reel/VIDEO_ID/ or https://fb.watch/VIDEO_ID/ or https://www.facebook.com/watch/?v=VIDEO_ID
      const facebookReelMatch = url.match(/(?:facebook\.com\/reel\/|fb\.watch\/)([^/?]+)/i);
      if (facebookReelMatch && facebookReelMatch[1]) {
        return facebookReelMatch[1];
      }
      const facebookWatchMatch = url.match(/[?&]v=([^&]+)/i);
      if (facebookWatchMatch && facebookWatchMatch[1]) {
        return facebookWatchMatch[1];
      }
      return null;
    }

    default:
      return null;
  }
}

/**
 * Validate if a URL is a valid social media post URL
 */
export function isValidPostUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const candidate = url.trim().replace(/#.*$/, '').replace(/\/+$/, '');
  const platform = extractPlatformFromUrl(candidate);
  if (!platform) {
    return false;
  }
  // TikTok share-sheet short links carry no extractable id but are valid — the
  // backend resolves the redirect to the canonical video URL.
  if (platform === 'tiktok' && isTikTokShortLink(candidate)) {
    return true;
  }
  const postId = extractPostIdFromUrl(candidate, platform);
  return postId !== null;
}
